import { identity } from 'rxjs';
import { isFileDataTransfer } from '../../helpers/dataTransfer';
import { clipSelection } from '../../helpers/elementHtmlEditor/clipboard';
import { flattenParagraphs } from '../../helpers/flattenParagraphs';
import { htmlToText } from '../../helpers/htmlToText';
import { createContentEditableResetService } from '../../services/dom/contentEditableResetService';
import { createElementParagraphTextUnderCursorService } from '../../services/dom/elementParagraphTextUnderCursorService';
import { createAutocompleteService } from '../../services/text/autocompleteService';
import { ContentEditableEditor, createContentEditableEditor } from '../base/contentEditableEditor';
import { EditorSpec } from '../base/editor';
import { ContentEditableEditorPlugin } from '../base/plugins/contentEditableEditorPlugin';
import { TextEditor, TextEditorServices } from './textEditor';

const FLATTEN_MAX_PARAGRAPH_COUNT = 100;

export interface ContentEditableTextEditor<S extends TextEditorServices = TextEditorServices>
  extends ContentEditableEditor<S>,
    TextEditor<S> {}

export type ContentEditableTextEditorOptions = {
  plugins?: ReadonlyArray<ContentEditableTextEditorPlugin>;
  shouldNativeDelete?: boolean;
  hasNewDeleteSelection?: boolean;
};

export interface ContentEditableTextEditorPlugin extends ContentEditableEditorPlugin {}

type ContentEditableTextEditorSpecCreator<S extends TextEditorServices> = (
  spec: EditorSpec<TextEditorServices>,
) => EditorSpec<S>;

export function createContentEditableTextEditor<S extends TextEditorServices>(
  options: ContentEditableTextEditorOptions,
  createSpec: ContentEditableTextEditorSpecCreator<S>,
): ContentEditableTextEditor<S>;
export function createContentEditableTextEditor(
  options?: ContentEditableTextEditorOptions,
): ContentEditableTextEditor;
export function createContentEditableTextEditor<S extends TextEditorServices = TextEditorServices>(
  options: ContentEditableTextEditorOptions = {},
  createSpec: ContentEditableTextEditorSpecCreator<S> = identity as ContentEditableTextEditorSpecCreator<S>,
): ContentEditableTextEditor<S> {
  const customPlugins = options.plugins || [];
  const plugins = [...customPlugins, createCoreAfterPlugin()];

  return createContentEditableEditor(
    {
      plugins,
      shouldNativeDelete: options.shouldNativeDelete,
      hasNewDeleteSelection: options.hasNewDeleteSelection,
    },
    (baseSpec, element$) => {
      const { services: baseServices, privateServices: basePrivateServices = [] } = baseSpec;
      const { selection: selectionService } = baseServices;

      const textUnderCursorService = createElementParagraphTextUnderCursorService(selectionService);
      const autocompleteService = createAutocompleteService(textUnderCursorService);
      const contentEditableResetService = createContentEditableResetService(element$, {
        shouldPreventDefaultStyleKeyboardShortcuts: true,
      });

      return createSpec({
        ...baseSpec,
        services: {
          ...baseServices,
          textUnderCursor: textUnderCursorService,
          autocomplete: autocompleteService,
        },
        privateServices: [contentEditableResetService, ...basePrivateServices],
      });
    },
  );
}

const createCoreAfterPlugin = (): ContentEditableEditorPlugin => ({
  processTree: element => {
    flattenParagraphs(element, {
      shouldConvertBlocksToParagraphs: true,
      maxParagraphCount: FLATTEN_MAX_PARAGRAPH_COUNT,
    });

    // Conservatively remove styles by removing style attributes from spans where it's the only attribute.
    for (const span of element.querySelectorAll('span[style]')) {
      if (span.attributes.length > 1) {
        continue;
      }

      span.removeAttribute('style');
    }
  },
  handleDataTransfer: (dataTransfer, editService) => {
    // If this appears to be a file upload/drop, then ignore any HTML versions and let the event bubble.
    if (isFileDataTransfer(dataTransfer)) {
      return false;
    }

    // Prefer plain text, if present.
    const text = dataTransfer.getData('text/plain');
    if (text) {
      editService.insertText(text);
      return true;
    }

    // Convert HTML to text, if present. This effectively disables data transfers containing only an <img> tag.
    const html = dataTransfer.getData('text/html');
    if (html) {
      editService.insertText(htmlToText(html));
      return true;
    }

    return false;
  },
  populateDataTransfer: (dataTransfer, element) => {
    const selection = element.ownerDocument && element.ownerDocument.getSelection();
    const clipping = clipSelection(element, selection);
    if (!clipping) {
      return false;
    }

    dataTransfer.setData('text/plain', clipping.text);
    return true;
  },
});
